import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, writeOrgFile } from '../../storage/FileSystemAdapter';
import SupplierList from './SupplierList';
import SupplierForm from './SupplierForm';
import ImportWizard from '../shared/ImportWizard';
import ConfirmDialog from '../shared/ConfirmDialog';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_SAVE_AND_LEAVE, TEXT_MUTED, TEXT_DISABLED, TEXT_HEADING, ERROR_BG, ERROR_TEXT, ERROR_BORDER, FONT_PAGE_TITLE, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY } from '../../theme';
import { CRITICALITY_LEVELS, SUPPLIER_TYPES } from '../../utils/enums';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:       'Gestión de proveedores',
    backToList:  '← Volver a la lista',
    clauseRef:   'Controles A.5.19–5.22 · ISO/IEC 27001:2022',
    loadError:   'Error al leer los datos. Comprueba el acceso a la carpeta.',
    retry:       'Reintentar',
    importCsv:   'Importar CSV',
    importTitle: 'Importar proveedores',
    newSupplier: '+ Nuevo proveedor',
    unsavedLabel:       'Cambios sin guardar',
    unsavedConfirm:     '¿Salir sin guardar los cambios?',
    leaveWithoutSaving: 'Salir sin guardar',
    saveAndLeave:       'Guardar y salir',
    formCancel:         'Cancelar',
    deleteBtn:          'Eliminar',
  },
  en: {
    title:       'Supplier management',
    backToList:  '← Back to list',
    clauseRef:   'Controls A.5.19–5.22 · ISO/IEC 27001:2022',
    loadError:   'Failed to read data. Check folder access.',
    retry:       'Retry',
    importCsv:   'Import CSV',
    importTitle: 'Import suppliers',
    newSupplier: '+ New supplier',
    unsavedLabel:       'Unsaved changes',
    unsavedConfirm:     'Leave without saving changes?',
    leaveWithoutSaving: 'Leave without saving',
    saveAndLeave:       'Save and leave',
    formCancel:         'Cancel',
    deleteBtn:          'Delete',
  },
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function SuppliersView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [suppliers,   setSuppliers]   = useState(null);  // null = loading
  const [formMode,    setFormMode]    = useState(null);  // null | 'create' | 'edit'
  const [editSupplier,setEditSupplier]= useState(null);
  const [loadError,   setLoadError]   = useState(false);
  const [showImport,  setShowImport]  = useState(false);
  const [dirty,       setDirty]       = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next successful form save reports back via onSaveResult instead
  // of running the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef             = useRef(false);
  const lastProcessedAppTickRef       = useRef(0);

  useScrollToTopOnFormOpen(formMode !== null);

  const loadSuppliers = () => {
    setLoadError(false);
    listOrgCollection(org.id, 'suppliers')
      .then((items) => {
        const sorted = [...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || ''));
        setSuppliers(sorted);
      })
      .catch(() => { setSuppliers([]); setLoadError(true); });
  };

  useEffect(() => {
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const handleFormSave = (fileId) => {
    if (fileId === null) {
      setFormMode(null);
      setEditSupplier(null);
      setDirty(false);
      setSaveRequestTick(0);
      loadSuppliers();
      return;
    }
    setDirty(false);
    setSaveRequestTick(0);
    loadSuppliers();
    if (pendingAppSaveRef.current) {
      pendingAppSaveRef.current = false;
      onSaveResult?.(true);
    }
  };

  const closeForm = () => { setFormMode(null); setEditSupplier(null); setDirty(false); };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          color: BTN_SAVE_AND_LEAVE,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formMode) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Import view ──────────────────────────────────────────────────────────────
  if (showImport && !formMode) {
    return (
      <div>
        <ImportWizard
          lang={lang}
          dedupeKey="name"
          onClose={() => setShowImport(false)}
          onImport={async (rows) => {
            const existing = await listOrgCollection(org.id, 'suppliers').catch(() => []);
            const year = new Date().getFullYear();
            const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const now = new Date().toISOString();

            const dayPrefix = `sup-${day}-`;
            let maxFileSeq = 0;
            existing.forEach(s => {
              if (s.id && s.id.startsWith(dayPrefix)) {
                const n = parseInt(s.id.slice(dayPrefix.length), 10);
                if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
              }
            });

            const supplierPrefix = (org.refPrefixes && org.refPrefixes.supplier) || 'sup';
            const yearPrefix = `${supplierPrefix}-${year}-`;
            let maxRefSeq = 0;
            existing.forEach(s => {
              if (s.ref && s.ref.startsWith(yearPrefix)) {
                const n = parseInt(s.ref.slice(yearPrefix.length), 10);
                if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
              }
            });

            let importedCount = 0;
            for (const row of rows) {
              const dup = existing.find(s => s.name && s.name.toLowerCase() === (row.name || '').toLowerCase());
              if (dup) continue;
              importedCount++;

              const fileId = `${dayPrefix}${String(++maxFileSeq).padStart(3, '0')}`;
              const ref = `${yearPrefix}${String(++maxRefSeq).padStart(3, '0')}`;

              const supplierObj = {
                id: fileId, orgId: org.id, ref,
                name: row.name || '', type: row.type || 'Other',
                criticality: row.criticality || 'Medium',
                description: '', address: row.address || null,
                owner: '',
                contactName: row.contactName || '',
                contactEmail: row.contactEmail || null,
                servicesProvided: Array.isArray(row.servicesProvided) ? row.servicesProvided : [],
                dataAccess: !!row.dataAccess,
                dataTypes: [],
                securityRequirementsIncluded: !!row.securityRequirementsIncluded,
                contractRef: row.contractRef || null,
                contractStartDate: row.contractStartDate || null,
                contractExpiry: row.contractExpiry || null,
                reviewFrequencyMonths: 12,
                lastReviewDate: row.lastReviewDate || null,
                lastReviewOutcome: row.lastReviewOutcome || null,
                nextReviewDate: '',
                linkedRiskIds: [], linkedControlIds: [], evidenceIds: [],
                linkedAssetIds: [],
                exitPlanExists: false,
                status: 'Active',
                notes: row.notes || '',
                createdAt: now, updatedAt: now,
              };
              await writeOrgFile(org.id, `suppliers/${fileId}.json`, supplierObj);
              existing.push(supplierObj);
            }
            loadSuppliers();
            return importedCount;
          }}
          columns={[
            { key: 'name',                        label: 'Name',                        required: true,  type: 'string' },
            { key: 'type',                        label: 'Type',                        required: false, type: 'string',  enum: SUPPLIER_TYPES, default: 'Other', caseInsensitive: true },
            { key: 'address',                     label: 'Address',                     required: false, type: 'string',  default: null },
            { key: 'criticality',                 label: 'Criticality',                 required: true,  type: 'string',  enum: CRITICALITY_LEVELS, caseInsensitive: true },
            { key: 'contactName',                 label: 'Contact Name',                required: false, type: 'string',  default: '' },
            { key: 'contactEmail',                label: 'Contact Email',               required: false, type: 'string',  default: null },
            { key: 'contractRef',                 label: 'Contract Reference',          required: false, type: 'string',  default: null },
            { key: 'contractStartDate',           label: 'Contract Start Date',         required: false, type: 'string',  default: null },
            { key: 'contractExpiry',              label: 'Contract Expiry',             required: false, type: 'string',  default: null },
            { key: 'servicesProvided',            label: 'Services Provided',           required: false, type: 'array',   default: [] },
            { key: 'dataAccess',                  label: 'Data Access',                 required: false, type: 'boolean', default: false },
            { key: 'securityRequirementsIncluded',label: 'Security Requirements',       required: false, type: 'boolean', default: false },
            { key: 'lastReviewDate',              label: 'Last Review Date',            required: false, type: 'string',  default: null },
            { key: 'lastReviewOutcome',           label: 'Last Review Outcome',         required: false, type: 'string',  enum: ['Satisfactory','Improvement required','Critical findings'], default: null, caseInsensitive: true },
            { key: 'notes',                       label: 'Notes',                       required: false, type: 'string',  default: '' },
          ]}
          templateFilename="import-suppliers.csv"
          title={{ es: t.importTitle, en: 'Import suppliers' }}
        />
      </div>
    );
  }

  // ── Form view ────────────────────────────────────────────────────────────────
  if (formMode) {
    return (
      <div>
        <button
          onClick={handleFormCancel}
          style={{
            ...FONT_LABEL,
            background: 'none', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, letterSpacing: '0.03em', padding: 0,
            marginBottom: '0.75rem', display: 'block',
          }}
        >
          {t.backToList}
        </button>
        <SupplierForm
          supplier={formMode === 'create' ? null : editSupplier}
          mode={formMode}
          org={org}
          lang={lang}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <h1 style={{ ...FONT_PAGE_TITLE, color: TEXT_HEADING, margin: 0 }}>
            {t.title}
          </h1>
          <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.15rem 0 0', letterSpacing: '0.03em' }}>
            {t.clauseRef}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <button
            onClick={() => setShowImport(true)}
            style={{
              ...BTN_SECONDARY,
              ...FONT_BUTTON,
              borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
            }}
          >
            {t.importCsv}
          </button>
          <button
            onClick={() => { setFormMode('create'); setDirty(false); }}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none', borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
            }}
          >
            {t.newSupplier}
          </button>
        </div>
      </div>

      {/* Load error banner */}
      {loadError && (
        <div style={{
          ...FONT_LABEL,
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`, borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
          color: ERROR_TEXT,
        }}>
          <span>{t.loadError}</span>
          <button
            onClick={loadSuppliers}
            style={{
              ...BTN_DESTRUCTIVE_OUTLINE,
              ...FONT_SMALL,
              borderRadius: 6,
              padding: '0.25rem 0.6rem', cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {t.retry}
          </button>
        </div>
      )}

      {/* List */}
      <SupplierList
        suppliers={suppliers}
        loading={suppliers === null}
        org={org}
        lang={lang}
        onNew={() => { setFormMode('create'); setDirty(false); }}
        onEdit={(s) => { setEditSupplier(s); setFormMode('edit'); setDirty(false); }}
        onView={(s) => { setEditSupplier(s); setFormMode('view'); setDirty(false); }}
        onNavigate={onNavigate}
        onSuppliersChanged={loadSuppliers}
      />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
